import { useState } from 'react';
import axios from 'axios';
import { AlertTriangle, Send, Power, PowerOff, CheckCircle, XCircle, Lock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';

const API = '/api';

export default function QuickAnnouncePage() {
  const [pin, setPin] = useState('');
  const [text, setText] = useState('');
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleSend = async () => {
    if (!pin || !text.trim()) return;
    setLoading(true);
    setStatus(null);
    try {
      await axios.post(`${API}/quick-announce`, { pin, text: text.trim() });
      setStatus({ type: 'success', msg: 'Upozornění bylo nastaveno!' });
      setText('');
    } catch (e) {
      setStatus({ type: 'error', msg: e.response?.status === 403 ? 'Neplatný PIN' : 'Chyba při odesílání' });
    }
    setLoading(false);
  };

  const handleOff = async () => {
    if (!pin) return;
    setLoading(true);
    setStatus(null);
    try {
      await axios.post(`${API}/quick-announce/off`, { pin });
      setStatus({ type: 'success', msg: 'Upozornění bylo vypnuto.' });
    } catch (e) {
      setStatus({ type: 'error', msg: e.response?.status === 403 ? 'Neplatný PIN' : 'Chyba' });
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-[#F8FAF8] flex items-center justify-center p-4">
      <Card className="w-full max-w-md bg-white border-[#E2E8F0] shadow-lg" data-testid="quick-announce-card">
        <CardHeader className="text-center pb-2">
          <div className="mx-auto w-12 h-12 bg-[#FEF3C7] rounded-2xl flex items-center justify-center mb-3">
            <AlertTriangle className="w-6 h-6 text-[#92400E]" />
          </div>
          <CardTitle className="text-xl font-bold text-[#052E16] font-['Manrope']">
            Rychlé upozornění
          </CardTitle>
          <p className="text-sm text-[#57534E] mt-1">
            Nastavte text informační lišty bez přihlášení
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-1.5">
            <Label className="flex items-center gap-1.5 text-sm font-medium text-[#1C1917]">
              <Lock className="w-3.5 h-3.5 text-[#57534E]" />
              PIN kód
            </Label>
            <Input
              type="password"
              inputMode="numeric"
              maxLength={6}
              value={pin}
              onChange={(e) => setPin(e.target.value)}
              placeholder="Zadejte PIN"
              data-testid="announce-pin-input"
              className="bg-white border-[#E2E8F0] text-[#1C1917] focus:border-[#16A34A] text-center text-lg tracking-widest"
            />
          </div>

          <div className="space-y-1.5">
            <Label className="text-sm font-medium text-[#1C1917]">Text upozornění</Label>
            <Input
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Např: Dnes od 15:00 odstávka serveru"
              data-testid="announce-text-input"
              className="bg-white border-[#E2E8F0] text-[#1C1917] focus:border-[#16A34A]"
              onKeyDown={(e) => e.key === 'Enter' && handleSend()}
            />
          </div>

          <div className="flex gap-2 pt-2">
            <Button
              onClick={handleSend}
              disabled={loading || !pin || !text.trim()}
              data-testid="announce-send-btn"
              className="flex-1 bg-[#16A34A] hover:bg-[#15803D] text-white font-semibold"
            >
              <Send className="w-4 h-4 mr-2" />
              Nastavit
            </Button>
            <Button
              onClick={handleOff}
              disabled={loading || !pin}
              variant="outline"
              data-testid="announce-off-btn"
              className="border-[#E2E8F0] text-[#57534E] hover:bg-[#FEE2E2] hover:text-[#991B1B] hover:border-[#FECACA]"
            >
              <PowerOff className="w-4 h-4 mr-2" />
              Vypnout
            </Button>
          </div>

          {status && (
            <div
              className={`flex items-center gap-2 p-3 rounded-xl text-sm font-medium ${
                status.type === 'success'
                  ? 'bg-[#F0FDF4] text-[#16A34A] border border-[#DCFCE7]'
                  : 'bg-[#FEE2E2] text-[#991B1B] border border-[#FECACA]'
              }`}
              data-testid="announce-status"
            >
              {status.type === 'success' ? <CheckCircle className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
              {status.msg}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
